import React, { useState } from 'react';
import { 
  FileText, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  AlertCircle,
  ChevronDown,
  ChevronRight,
  AlertTriangle
} from 'lucide-react';
import { 
  useGetPageReferenceValidationsQuery,
  type PageReferenceValidationGroup,
  type PageReferenceValidationItem,
} from '../../redux/api/qaApi';
import { useAppSelector } from '../../redux/hooks';

const PageReferencesTab: React.FC = () => {
  // Get site_id from Redux
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const siteId = currentSite?.site_id;

  // Accordion state - track which pages are expanded
  const [expandedPages, setExpandedPages] = useState<Set<number>>(new Set());

  const togglePage = (pageNumber: number) => {
    setExpandedPages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pageNumber)) {
        newSet.delete(pageNumber);
      } else {
        newSet.add(pageNumber);
      }
      return newSet;
    });
  };

  // Fetch page reference validations
  const { data: validationData, isLoading, error } = useGetPageReferenceValidationsQuery(
    siteId ? { site_id: siteId } : undefined,
    { skip: !siteId }
  );

  const totalReferences = validationData?.total_references || 0;
  const totalPages = validationData?.total_pages || 0;
  const totalValid = validationData?.total_valid || 0;
  const totalInvalid = validationData?.total_invalid || 0;
  const pages = validationData?.pages || [];

  return (
    <div className="eg-page-shell flex flex-col">
      {/* Header */}
      <div className="eg-toolbar-header">
        <div className="flex items-center justify-between mb-4 px-6 pt-4">
          <div>
            <h2 className="text-xl font-semibold eg-text-primary mb-1">Page Reference Validation</h2>
            <p className="text-sm eg-text-secondary">
              {totalReferences} references across {totalPages} pages
            </p>
          </div>
        </div>

        {/* Summary Stats */}
        {validationData && (
          <div className="flex flex-wrap gap-4 px-6 pb-4">
            <div className="flex items-center gap-2 px-3 py-2 bg-red-400/20 text-red-400 rounded-lg border border-red-400/30">
              <XCircle size={18} />
              <span className="font-medium">{totalInvalid} invalid</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-green-400/20 text-green-400 rounded-lg border border-green-400/30">
              <CheckCircle size={18} />
              <span className="font-medium">{totalValid} valid</span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="eg-page-content flex-1 overflow-auto">
        {!siteId ? (
          <div className="flex items-center justify-center py-12">
            <AlertCircle className="w-8 h-8 eg-text-secondary mr-3" />
            <div className="eg-text-primary">
              <p className="font-semibold">No Site Selected</p>
              <p className="text-sm eg-text-secondary mt-1">
                Please select a site from the sidebar to view page reference validations
              </p>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-engramiq-green" />
            <span className="ml-3 eg-text-primary">Loading page reference validations...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <AlertCircle className="w-8 h-8 text-accent-red mr-3" />
            <div className="eg-text-primary">
              <p className="font-semibold">Error loading page reference validations</p>
              <p className="text-sm eg-text-secondary mt-1">
                {error && 'data' in error ? JSON.stringify(error.data) : 'Unknown error'}
              </p>
            </div>
          </div>
        ) : pages.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <FileText className="w-8 h-8 eg-text-secondary mr-3" />
            <p className="eg-text-primary">No page references found</p>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            {/* All Pages - Grouped by Page */}
            {pages.map((pageGroup) => (
              <PageGroup
                key={pageGroup.page_number}
                pageGroup={pageGroup}
                isExpanded={expandedPages.has(pageGroup.page_number)}
                onToggle={() => togglePage(pageGroup.page_number)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/** Page Group Component */
interface PageGroupProps {
  pageGroup: PageReferenceValidationGroup;
  isExpanded: boolean;
  onToggle: () => void;
}

const PageGroup: React.FC<PageGroupProps> = ({ pageGroup, isExpanded, onToggle }) => {
  // Separate valid and invalid references
  const validRefs = pageGroup.references.filter(r => r.is_valid);
  const invalidRefs = pageGroup.references.filter(r => !r.is_valid);

  return (
    <div className="eg-card-elevated overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-[var(--color-surface-hover)] transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-engramiq-green flex-shrink-0" />
          ) : (
            <ChevronRight className="w-5 h-5 text-engramiq-green flex-shrink-0" />
          )}
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold eg-text-primary">
                PAGE {pageGroup.page_number}
              </span>
              {pageGroup.sheet_id && (
                <span className="text-sm eg-text-secondary">— {pageGroup.sheet_id}</span>
              )}
            </div>
            <div className="text-sm eg-text-secondary mt-1">
              {pageGroup.total_refs} refs • {pageGroup.invalid_refs} invalid • {pageGroup.valid_refs} valid
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pageGroup.invalid_refs > 0 && (
            <span className="px-2 py-1 rounded text-xs font-medium border bg-red-400/20 text-red-400 border-red-400/30">
              {pageGroup.invalid_refs} invalid
            </span>
          )}
          {pageGroup.valid_refs > 0 && (
            <span className="px-2 py-1 rounded text-xs font-medium border bg-green-400/20 text-green-400 border-green-400/30">
              {pageGroup.valid_refs} valid
            </span>
          )}
        </div>
      </button>
      {isExpanded && (
        <div className="px-6 py-4 border-t border-[var(--color-border)] space-y-4">
          {/* Invalid References */}
          {invalidRefs.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
                <XCircle size={16} />
                Invalid ({invalidRefs.length})
              </h4>
              <div className="space-y-2">
                {invalidRefs.map((ref, idx) => (
                  <ReferenceItem key={`${pageGroup.page_number}-invalid-${idx}-${ref.reference_text}`} reference={ref} />
                ))}
              </div>
            </div>
          )}
          
          {/* Valid References */}
          {validRefs.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-green-400 mb-2 flex items-center gap-2">
                <CheckCircle size={16} />
                Valid ({validRefs.length})
              </h4>
              <div className="space-y-2">
                {validRefs.map((ref, idx) => (
                  <ReferenceItem key={`${pageGroup.page_number}-valid-${idx}-${ref.reference_text}`} reference={ref} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Reference Item Component */
interface ReferenceItemProps {
  reference: PageReferenceValidationItem;
}

const ReferenceItem: React.FC<ReferenceItemProps> = ({ reference }) => {
  const isValid = reference.is_valid;
  const statusIcon = isValid ? (
    <CheckCircle size={16} className="text-green-400" />
  ) : (
    <XCircle size={16} className="text-red-400" />
  );

  return (
    <div className={`border rounded-lg p-3 ${
      isValid
        ? 'border-green-400/30 bg-green-400/10'
        : 'border-red-400/30 bg-red-400/10'
    }`}>
      <div className="flex items-start gap-3">
        {statusIcon}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium eg-text-primary text-sm">
              {reference.reference_text}
            </span>
            {reference.target_page && (
              <span className="text-xs eg-text-secondary">
                → Page {reference.target_page}
              </span>
            )}
          </div>
          {reference.validation_description && (
            <p className="text-xs eg-text-secondary mt-1">
              {reference.validation_description}
            </p>
          )}
          <div className="flex items-center gap-3 mt-2 text-xs eg-text-secondary">
            {reference.target_found_in_toc && (
              <span className="flex items-center gap-1">
                <CheckCircle size={12} className="text-green-400" />
                Found in TOC
              </span>
            )}
            <span>{reference.confidence}% confidence</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageReferencesTab;
